/**
 * MethodologyPicker — elegir la metodología de una plantilla (OT-0055).
 *
 * Un solo selector para todos los lugares donde se decide la pertenencia:
 * la cabecera de los editores y los diálogos de crear y clonar. Ofrece las
 * metodologías del tenant (las del producto no admiten plantillas por API),
 * "Genérica" cuando el tipo lo permite (todo menos el ciclo de vida) y,
 * opcionalmente, crear una metodología ahí mismo.
 *
 * Controlado: `value` es el id de la metodología o `null` (genérica).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMethodologies } from '../../../hooks/useMethodologies'
import { MethodologyFormDialog } from './MethodologyFormDialog'

const GENERIC = ''
const CREATE = '__create__'

interface MethodologyPickerProps {
  value: string | null
  onChange: (methodologyId: string | null) => void
  /** Falso para el ciclo de vida: siempre pertenece a una metodología. */
  allowGeneric: boolean
  /** Ofrece "Nueva metodología…" y la elige al crearla. */
  allowCreate?: boolean
  disabled?: boolean
  id?: string
  className?: string
}

export function MethodologyPicker({
  value,
  onChange,
  allowGeneric,
  allowCreate = false,
  disabled = false,
  id,
  className,
}: MethodologyPickerProps) {
  const { t } = useTranslation('templates')
  const { methodologies } = useMethodologies()
  const [createOpen, setCreateOpen] = useState(false)

  // Las del tenant que admiten plantillas; la actual siempre aparece,
  // aunque esté archivada, para que el valor sea representable.
  const options = methodologies.filter(
    (m) => m.tenant_id !== null && (m.status !== 'archived' || m.id === value),
  )

  return (
    <>
      <select
        id={id}
        value={value ?? GENERIC}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value
          if (next === CREATE) setCreateOpen(true)
          else onChange(next === GENERIC ? null : next)
        }}
        className={
          className ??
          'bg-canvas border border-line rounded-md px-2 py-1 text-xs text-ink focus:outline-none focus:border-brand disabled:opacity-60'
        }
      >
        {allowGeneric ? (
          <option value={GENERIC}>{t('methodologies.picker.generic')}</option>
        ) : value === null ? (
          <option value={GENERIC} disabled>
            {t('methodologies.picker.choose')}
          </option>
        ) : null}
        {options.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
        {allowCreate ? (
          <option value={CREATE}>{t('methodologies.picker.create')}</option>
        ) : null}
      </select>
      <MethodologyFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(created) => onChange(created.id)}
      />
    </>
  )
}
